import type {
  Communication,
  QueueRecommendation,
} from '@entities/communication/model/types';
import type {Employee} from '@entities/employee/model/types';
import type {KpiSnapshot} from './types';
import type {AnalyticsChannelFilter, Period} from '@lib/atoms';

/**
 * Pure-функции для агрегирования реальных коммуникаций бэка под UI-нужды,
 * для которых на бэке пока нет отдельных эндпоинтов (kpi/employees/queue/
 * rating/group/objections/refusals/calls-dynamics/score-distribution).
 *
 * Все агрегаты считаются после применения adapter'а — то есть в шкале фронта:
 * `Communication.score` 0–10.
 */

const avg = (nums: number[]): number =>
  nums.length === 0 ? 0 : nums.reduce((s, v) => s + v, 0) / nums.length;

const pct = (numerator: number, denominator: number): number =>
  denominator === 0 ? 0 : (numerator / denominator) * 100;

const periodDays = (period: Period): number => {
  switch (period) {
    case 'day':
      return 1;
    case 'week':
      return 7;
    case 'month':
      return 30;
    case 'quarter':
      return 90;
    case 'year':
      return 365;
    default:
      return 7;
  }
};

/**
 * Попадает ли коммуникация (или строка очереди) в таб типа коммуникации
 * дашборда. Очные / онлайн — по типу встречи (канал); встреча без типа видна
 * только во «Всех коммуникациях», как срез аналитики на бэке.
 */
export const matchesAnalyticsChannel = (
  item: {
    channelType?: 'phone' | 'meeting';
    meetingType?: 'offline' | 'online' | null;
  },
  channel: AnalyticsChannelFilter,
): boolean => {
  if (channel === 'all') return true;
  if (channel === 'phone') return (item.channelType ?? 'phone') === 'phone';
  if (item.channelType !== 'meeting') return false;
  return channel === 'online-meeting'
    ? item.meetingType === 'online'
    : item.meetingType === 'offline';
};

export const filterByPeriod = (
  comms: Communication[],
  period: Period,
): Communication[] => {
  const cutoff = Date.now() - periodDays(period) * 24 * 60 * 60 * 1000;
  return comms.filter((c) => {
    const t = Date.parse(c.dateTime);
    return Number.isFinite(t) && t >= cutoff;
  });
};

// ─── Базовые агрегаты (используются и за пределами дашборда) ────────────────

export const employeesFromCommunications = (
  comms: Communication[],
): Employee[] => {
  const byMgr = new Map<string, Communication[]>();
  for (const c of comms) {
    if (!c.managerId) continue;
    const arr = byMgr.get(c.managerId) ?? [];
    arr.push(c);
    byMgr.set(c.managerId, arr);
  }

  return Array.from(byMgr.entries()).map(([id, mgrComms]) => {
    const targeted = mgrComms.filter((c) => c.isTargeted);
    const validScores = targeted.map((c) => c.score).filter((s) => s > 0);
    const goals = targeted.filter((c) => c.goalAchieved);
    const allObjections = targeted.flatMap((c) => c.objections);
    const allHandled = targeted.flatMap((c) => c.objectionsHandled ?? []);

    return {
      id,
      name: mgrComms[0].manager || id,
      role: 'manager' as const,
      calls: targeted.length,
      callsPlan: Math.max(targeted.length, 20),
      score: avg(validScores),
      scoreDelta: 0,
      conv: pct(goals.length, targeted.length),
      convDelta: 0,
      objectionsTotal: allObjections.length,
      objIdent: pct(allObjections.length, allObjections.length),
      objHandled: pct(allHandled.length, allObjections.length),
      scriptCompliance: pct(
        targeted.filter((c) => c.scriptCompliance).length,
        targeted.length,
      ),
      trend: 'stable',
      scoreHistory: validScores.slice(-7),
    };
  });
};

export const employeeFromCommunications = (
  comms: Communication[],
  id: string,
): Employee | null => {
  const list = employeesFromCommunications(
    comms.filter((c) => c.managerId === id),
  );
  return list[0] ?? null;
};

export const kpiFromCommunications = (
  comms: Communication[],
  period: Period,
): KpiSnapshot => {
  const inRange = filterByPeriod(comms, period);
  const targeted = inRange.filter((c) => c.isTargeted);
  const validScores = targeted.map((c) => c.score).filter((s) => s > 0);
  const goals = targeted.filter((c) => c.goalAchieved);

  return {
    period,
    callsTotal: targeted.length,
    callsPlan: Math.max(inRange.length, 50),
    callsDelta: 0,
    avgScore: avg(validScores),
    scoreDelta: 0,
    conversion: pct(goals.length, targeted.length),
    convDelta: 0,
    withResult: targeted.filter((c) => c.result !== null).length,
    withResultDelta: 0,
    withAgreement: targeted.filter((c) => c.agreementReached).length,
    withAgreementDelta: 0,
    scoreHistory: validScores.slice(-30),
  };
};

export const queueFromCommunications = (
  comms: Communication[],
): QueueRecommendation[] => {
  const candidates = comms
    .filter((c) => c.isTargeted && c.score > 0)
    .filter((c) => c.score < 7 || !c.saleMade)
    .sort((a, b) => a.score - b.score)
    .slice(0, 10);

  return candidates.map((c) => ({
    id: `Q-${c.id}`,
    communicationId: c.id,
    priority: c.score < 4 ? 1 : c.score < 6 ? 2 : 3,
    queueType:
      c.score < 4 ? 'urgent' : c.score < 6 ? 'management' : 'practices',
    problem: c.recommendation || c.summary || 'Низкая оценка',
    client: c.client || '—',
    managerId: c.managerId,
    manager: c.manager,
    age: 'недавно',
    recommendation:
      c.recommendation || 'Прослушать звонок и обсудить с менеджером.',
    score: c.score,
    direction: c.direction,
  }));
};

// ─── Дашбордовые типы ──────────────────────────────────────────────────────

/** KPI-карточки дашборда (шкала /10). */
export interface DashKpi {
  /** Количество (целевых) звонков за период. 0 ⇒ метрики недоступны (UI рисует «—» без тренда). */
  callsTotal: number;
  /** Дельта количества звонков к прошлому периоду (как прочие *Delta). */
  callsTotalDelta: number | null;
  avgScore: number | null;
  avgScoreDelta: number | null;
  withResult: number | null;
  withResultDelta: number | null;
  conversion: number | null;
  conversionDelta: number | null;
  withAgreement: number | null;
  withAgreementDelta: number | null;
}

export interface RatingRow {
  managerId: string;
  managerFull: string;
  sub?: string;
  value: number;
  rankChange: number;
}

export interface GroupAnalyticsRow {
  managerId: string;
  teamSid?: string;
  branchName?: string;
  full: string;
  calls: number;
  callsPlan: number;
  targeted: number;
  successful: number;
  /** CR в целевое действие (goalAchieved) от целевых, %. */
  crGoalAction: number;
  avgScore: number;
  objectionsTotal: number;
  objectionsHandled: number | null;
  script: number;
}

export interface ObjectionRow {
  name: string;
  count: number;
  share: number;
  handled: number;
}

export interface RefusalRow {
  name: string;
  count: number;
  share: number;
}

export interface CallsDay {
  /** День недели — «Пн», «Вт», … */
  label: string;
  /** ISO-дата дня — «2026-08-04» (для tooltip'а с полной датой). */
  date: string;
  value: number;
}

export type ScoreBucketColor =
  | 'green'
  | 'lime'
  | 'orange'
  | 'red-orange'
  | 'red';

export interface ScoreBucket {
  range: string;
  count: number;
  color: ScoreBucketColor;
}

export interface UrgentQueueItem {
  id: string;
  callId: string;
  priority: 1 | 2 | 3;
  problemTitle: string;
  problemDescription: string;
  managerId: string;
  managerShort: string;
  managerFull: string;
  /** Управленческая подпись директорской строки; менеджер остаётся владельцем звонка. */
  leadShort?: string;
  branchName?: string;
  clientShort: string;
  score: number;
  ageMinutes: number;
  direction: 'incoming' | 'outgoing';
  channelType: 'phone' | 'meeting';
  /** Тип встречи по `metadata.channel` (null — не встреча или тип не определён). */
  meetingType?: 'online' | 'offline' | null;
  recommendation: string;
  context: string;
  tags: string[];
  /** Ред-флаг на звонке (ТЗ 3.4): тег рисуется визуально отличным от негатива. */
  isRedFlag?: boolean;
}

// ─── Дашбордовые агрегаты ──────────────────────────────────────────────────

/** KPI для дашборда (шкала /10). */
export const dashKpiFromCommunications = (
  comms: Communication[],
  period: Period,
): DashKpi => {
  const inRange = filterByPeriod(comms, period);
  const targeted = inRange.filter((c) => c.isTargeted);
  const validScores = targeted.map((c) => c.score).filter((s) => s > 0);
  const goals = targeted.filter((c) => c.goalAchieved);
  const withResultCount = targeted.filter((c) => c.result !== null).length;
  const withAgreement = targeted.filter((c) => c.agreementReached).length;

  return {
    callsTotal: targeted.length,
    callsTotalDelta: 0,
    avgScore: avg(validScores),
    avgScoreDelta: 0,
    withResult: Math.round(pct(withResultCount, targeted.length)),
    withResultDelta: 0,
    conversion: Math.round(pct(goals.length, targeted.length)),
    conversionDelta: 0,
    withAgreement: Math.round(pct(withAgreement, targeted.length)),
    withAgreementDelta: 0,
  };
};

/** Рейтинг по средней оценке (шкала /10). */
export const ratingByScore = (comms: Communication[]): RatingRow[] => {
  const emp = employeesFromCommunications(comms);
  return emp
    .map((e) => ({
      managerId: e.id,
      managerFull: e.name,
      sub: `${e.calls} ${pluralCalls(e.calls)}`,
      value: e.score,
      rankChange: 0,
    }))
    .sort((a, b) => b.value - a.value);
};

/** Рейтинг по конверсии (%). */
export const ratingByConversion = (comms: Communication[]): RatingRow[] => {
  const emp = employeesFromCommunications(comms);
  return emp
    .map((e) => ({
      managerId: e.id,
      managerFull: e.name,
      // Подпись с числом звонков — как в рейтинге по оценке: чтобы строки и высоты
      // двух таблиц рейтинга совпадали (выравнивание).
      sub: `${e.calls} ${pluralCalls(e.calls)}`,
      value: Math.round(e.conv),
      rankChange: 0,
    }))
    .sort((a, b) => b.value - a.value);
};

/** Расширенная таблица «Аналитика по группе». Знаменатель — targeted (§1). */
export const groupAnalyticsFromCommunications = (
  comms: Communication[],
): GroupAnalyticsRow[] => {
  const emp = employeesFromCommunications(comms);
  const goalByMgr = new Map<string, number>();
  const targetedByMgr = new Map<string, number>();
  const objectionsByMgr = new Map<string, number>();
  for (const c of comms) {
    if (!c.managerId) continue;
    if (c.isTargeted) {
      targetedByMgr.set(c.managerId, (targetedByMgr.get(c.managerId) ?? 0) + 1);
      objectionsByMgr.set(
        c.managerId,
        (objectionsByMgr.get(c.managerId) ?? 0) + c.objections.length,
      );
    }
    if (c.isTargeted && c.goalAchieved) {
      goalByMgr.set(c.managerId, (goalByMgr.get(c.managerId) ?? 0) + 1);
    }
  }
  return emp.map((e) => {
    const targeted = targetedByMgr.get(e.id) ?? 0;
    const goals = goalByMgr.get(e.id) ?? 0;
    const objTotal = objectionsByMgr.get(e.id) ?? 0;
    return {
      managerId: e.id,
      full: e.name,
      calls: e.calls,
      callsPlan: e.callsPlan,
      targeted,
      successful: goals,
      crGoalAction: Math.round(pct(goals, targeted)),
      avgScore: e.score,
      objectionsTotal: objTotal,
      objectionsHandled: objTotal === 0 ? null : Math.round(e.objHandled),
      script: Math.round(e.scriptCompliance),
    };
  });
};

/** Стат-таблица возражений (агрегат по name + handled%).
 *
 * `total` — сколько возражений выявлено всего; `unhandledTotal` — сколько из них
 * НЕ отработано. UI различает три состояния: total=0 (нет данных), unhandledTotal=0
 * при total>0 (всё отработано — позитив), иначе показывает строки. */
export const objectionsStats = (
  comms: Communication[],
): {total: number; unhandledTotal: number; rows: ObjectionRow[]} => {
  const all = comms.flatMap((c) => c.objections).filter(Boolean);
  const allHandled = comms
    .flatMap((c) => c.objectionsHandled ?? [])
    .filter(Boolean);
  const total = all.length;
  const unhandledTotal = total - allHandled.length;
  if (total === 0) return {total: 0, unhandledTotal: 0, rows: []};

  const handledSet = new Set(allHandled);
  const byName = new Map<string, {count: number; handled: number}>();
  for (const o of all) {
    const cur = byName.get(o) ?? {count: 0, handled: 0};
    cur.count += 1;
    if (handledSet.has(o)) cur.handled += 1;
    byName.set(o, cur);
  }

  const rows = Array.from(byName.entries())
    .map(([name, {count, handled}]) => ({
      name,
      count,
      share: Number(((count / total) * 100).toFixed(1)),
      handled: Math.round((handled / count) * 100),
    }))
    .sort((a, b) => b.count - a.count);

  return {total, unhandledTotal, rows};
};

/** Причины отказа — из `refusalReason` (§5 контракта дашборда).
 *
 * `targetedTotal` — сколько целевых звонков было в выборке. UI различает: total>0
 * (показываем причины), total=0 при targetedTotal>0 (потерь нет — позитив),
 * targetedTotal=0 (нет звонков — пусто). */
export const refusalsStats = (
  comms: Communication[],
): {total: number; targetedTotal: number; rows: RefusalRow[]} => {
  const targetedTotal = comms.filter((c) => c.isTargeted).length;
  const refused = comms.filter(
    (c) => c.refusalReason !== null && c.refusalReason !== undefined,
  );
  const total = refused.length;
  if (total === 0) return {total: 0, targetedTotal, rows: []};

  const byReason = new Map<string, number>();
  for (const c of refused) {
    const reason = c.refusalReason ?? 'Не указана';
    byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
  }

  const rows = Array.from(byReason.entries())
    .map(([name, count]) => ({
      name,
      count,
      share: Number(((count / total) * 100).toFixed(2)),
    }))
    .sort((a, b) => b.count - a.count);

  return {total, targetedTotal, rows};
};

/** Причины ред-флагов (ТЗ 3.5): фасет по образцу отказов.
 *
 * Без гейта isTargeted (флаг — сквозной сигнал); unknown-значения — как есть.
 * В балл и рейтинги не входит. */
export const redFlagStats = (
  comms: Communication[],
): {total: number; rows: RefusalRow[]} => {
  const flagged = comms.filter((c) => c.redFlag === true);
  const total = flagged.length;
  if (total === 0) return {total: 0, rows: []};

  const byReason = new Map<string, number>();
  for (const c of flagged) {
    const reason = c.redFlagReason?.trim() ? c.redFlagReason! : 'Не указана';
    byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
  }

  const rows = Array.from(byReason.entries())
    .map(([name, count]) => ({
      name,
      count,
      share: Number(((count / total) * 100).toFixed(2)),
    }))
    .sort((a, b) => b.count - a.count);

  return {total, rows};
};

/** Звонки по дням недели — последние 13 дней (как в эталоне). */
export const callsDynamicsByDay = (comms: Communication[]): CallsDay[] => {
  const days: CallsDay[] = [];
  const labels = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  for (let i = 12; i >= 0; i--) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - i);
    const start = date.getTime();
    const end = start + 24 * 60 * 60 * 1000;
    const count = comms.filter((c) => {
      const t = Date.parse(c.dateTime);
      return t >= start && t < end;
    }).length;
    const iso = date.toISOString().slice(0, 10);
    days.push({label: labels[date.getDay()], date: iso, value: count});
  }
  return days;
};

/** Распределение по баллам, шкала /10. */
export const scoreDistribution = (comms: Communication[]): ScoreBucket[] => {
  const buckets: ScoreBucket[] = [
    {range: '8.0 — 10.0', count: 0, color: 'green'},
    {range: '6.0 — 8.0', count: 0, color: 'lime'},
    {range: '4.0 — 6.0', count: 0, color: 'orange'},
    {range: '< 4.0', count: 0, color: 'red'},
  ];
  for (const c of comms) {
    if (c.score <= 0) continue;
    if (c.score >= 8.0) buckets[0].count++;
    else if (c.score >= 6.0) buckets[1].count++;
    else if (c.score >= 4.0) buckets[2].count++;
    else buckets[3].count++;
  }
  return buckets;
};

const hasUnhandledObjection = (c: Communication): boolean =>
  c.objections.length > (c.objectionsHandled?.length ?? 0);

// Теги-маркеры причин срочности (§4 контракта дашборда).
const TAG_NEGATIVE = 'Негатив';
const TAG_MISSED_DEAL = 'Упущенная сделка';
const TAG_UNHANDLED_OBJECTION = 'Неотработанное возражение';
const TAG_LOW_SCORE = 'Низкая оценка';
// Фолбэк-тег ред-флага — когда причина пуста (зеркало бэкенд-фолбэка, ТЗ 3.4).
const TAG_RED_FLAG_FALLBACK = 'Красный флаг';

/** Теги срочности звонка — зеркалит бэкенд `compute_queue_priority` (§4).
 * Ред-флаг — сквозной сигнал поверх всех масок (ТЗ 3.3): обходит isTargeted/score/temperature.
 * Пустой массив = звонок не срочный (в очередь не попадает). */
const computeQueueTags = (c: Communication): string[] => {
  // Ред-флаг — до масок: тег = reason (пустой → фолбэк), приоритет критичный.
  if (c.redFlag === true) {
    const reason = c.redFlagReason?.trim();
    return [reason ? reason : TAG_RED_FLAG_FALLBACK];
  }
  if (!c.isTargeted) return [];
  if (c.score <= 0) return [];
  const lowScore = c.score < 4;
  const unhandled = hasUnhandledObjection(c);
  const negative = c.withoutConflict === false && lowScore;
  const missedDeal = c.temperature === 'hot' && !c.goalAchieved;
  const unhandledObjection = lowScore && unhandled;

  if (!lowScore && !missedDeal) return [];

  // Холодные лиды не срочные, кроме тега Негатив (конфликт разбираем всегда).
  if (c.temperature === 'cold' && !negative) return [];

  const tags: string[] = [];
  if (negative) tags.push(TAG_NEGATIVE);
  if (missedDeal) tags.push(TAG_MISSED_DEAL);
  if (unhandledObjection) tags.push(TAG_UNHANDLED_OBJECTION);
  if (lowScore) tags.push(TAG_LOW_SCORE);
  return tags;
};

/** Условие попадания в очередь «требуют внимания». */
const qualifiesForUrgentQueue = (c: Communication): boolean =>
  computeQueueTags(c).length > 0;

/** Приоритет: 1 — критичный (ред-флаг/Негатив/Упущенная сделка), 2 — тёплый. */
const computeUrgentPriority = (c: Communication): 1 | 2 => {
  // Ред-флаг — критичный по определению (зеркало бэкенд-приоритета, ТЗ 3.3).
  if (c.redFlag === true) return 1;
  const tags = computeQueueTags(c);
  return tags.includes(TAG_NEGATIVE) || tags.includes(TAG_MISSED_DEAL) ? 1 : 2;
};

/** Очередь «требуют внимания» — для табы urgent дашборда (шкала /10). */
export const urgentQueueFromCommunications = (
  comms: Communication[],
): UrgentQueueItem[] => {
  const now = Date.now();

  return comms
    .filter(qualifiesForUrgentQueue)
    .sort((a, b) => {
      const pa = computeUrgentPriority(a);
      const pb = computeUrgentPriority(b);
      if (pa !== pb) return pa - pb; // 1 → 2
      const ageA = Date.parse(a.dateTime);
      const ageB = Date.parse(b.dateTime);
      if (ageA !== ageB) return ageA - ageB; // старше (меньше ts) → выше
      return a.score - b.score; // хуже сверху
    })
    .slice(0, 300)
    .map((c) => {
      const priority = computeUrgentPriority(c);
      const ageMs = Math.max(0, now - Date.parse(c.dateTime));
      const ageMinutes = Math.round(ageMs / 60000);

      const tags = computeQueueTags(c);

      return {
        id: `uq-${c.id}`,
        callId: c.id,
        priority,
        isRedFlag: c.redFlag === true,
        problemTitle: c.summary?.slice(0, 80) || 'Звонок требует внимания',
        problemDescription:
          c.summary ||
          c.resultText ||
          'Содержание звонка обрабатывается AI-аналитиком.',
        managerId: c.managerId,
        managerShort: c.manager,
        managerFull: c.manager,
        clientShort: c.client || '—',
        score: c.score,
        ageMinutes,
        direction: c.direction === 'in' ? 'incoming' : 'outgoing',
        channelType: c.channelType ?? 'phone',
        meetingType: c.meetingType ?? null,
        recommendation:
          c.recommendation ||
          'Прослушать звонок и обсудить с менеджером пропущенные возражения.',
        context: c.summary || c.resultText || '—',
        tags,
      };
    });
};

/** Управленческие решения: средний/низкий балл без горячей срочности. */
export const managementQueueFromCommunications = (
  comms: Communication[],
): UrgentQueueItem[] => {
  const now = Date.now();
  return comms
    .filter((c) => {
      if (!c.isTargeted || c.score <= 0 || c.goalAchieved) return false;
      if (qualifiesForUrgentQueue(c)) return false; // уже в urgent — не дублируем
      const score10 = c.score;
      return c.score < 7.0; // средний и ниже без достигнутой цели → управленческий разбор
    })
    .sort((a, b) => a.score - b.score)
    .slice(0, 30)
    .map((c) => {
      const score10 = c.score;
      const priority: 1 | 2 | 3 = c.score < 4.0 ? 2 : 3;
      const ageMinutes = Math.round(
        Math.max(0, now - Date.parse(c.dateTime)) / 60000,
      );
      const tags = [
        ...(c.temperature === 'cold' ? ['холодный лид'] : []),
        ...(c.score < 5.0 ? ['низкий балл'] : ['средний балл']),
        ...c.objections.filter((o) => !c.objectionsHandled?.includes(o)),
      ].slice(0, 3);
      return {
        id: `mq-${c.id}`,
        callId: c.id,
        priority,
        problemTitle: c.summary?.slice(0, 80) || 'Нестандартный кейс',
        problemDescription:
          c.summary ||
          c.resultText ||
          'Содержание звонка обрабатывается AI-аналитиком.',
        managerId: c.managerId,
        managerShort: c.manager,
        managerFull: c.manager,
        clientShort: c.client || '—',
        score: score10,
        ageMinutes,
        direction: c.direction === 'in' ? 'incoming' : 'outgoing',
        channelType: c.channelType ?? 'phone',
        recommendation:
          c.recommendation ||
          'Разобрать звонок с менеджером, выявить системную причину.',
        context: c.summary || c.resultText || '—',
        tags,
      };
    });
};

/** Лучшие практики: высокий балл + цель достигнута — масштабировать на команду. */
export const practicesQueueFromCommunications = (
  comms: Communication[],
): UrgentQueueItem[] => {
  const now = Date.now();
  return comms
    .filter((c) => {
      if (!c.isTargeted || c.score <= 0) return false;
      // Звонок с ред-флагом — не эталон, даже при высоком балле (ТЗ 3.3: он в urgent).
      if (c.redFlag === true) return false;
      return c.goalAchieved && c.score >= 7.0;
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 30)
    .map((c) => {
      const score10 = c.score;
      const priority: 1 | 2 | 3 = c.score >= 9.0 ? 1 : c.score >= 8.0 ? 2 : 3;
      const ageMinutes = Math.round(
        Math.max(0, now - Date.parse(c.dateTime)) / 60000,
      );
      const tags = [
        c.score >= 9.0 ? 'топ-результат' : 'хороший звонок',
        ...(c.promoApplied ? ['промо применён'] : []),
        ...(c.temperature === 'hot' ? ['горячий клиент'] : []),
      ].slice(0, 3);
      return {
        id: `pq-${c.id}`,
        callId: c.id,
        priority,
        problemTitle: c.summary?.slice(0, 80) || 'Эталонный звонок',
        problemDescription:
          c.summary ||
          c.resultText ||
          'Содержание звонка обрабатывается AI-аналитиком.',
        managerId: c.managerId,
        managerShort: c.manager,
        managerFull: c.manager,
        clientShort: c.client || '—',
        score: score10,
        ageMinutes,
        direction: c.direction === 'in' ? 'incoming' : 'outgoing',
        channelType: c.channelType ?? 'phone',
        recommendation:
          c.recommendation || 'Масштабировать приём на всю команду.',
        context: c.summary || c.resultText || '—',
        tags,
      };
    });
};

const pluralCalls = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'звонок';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100))
    return 'звонка';
  return 'звонков';
};
